import { useMemo, useState } from 'react';

import type { GestureResponderEvent } from 'react-native';

type Id = string;

type NativeLikeEvent = {
  shiftKey?: boolean;
  metaKey?: boolean;
  ctrlKey?: boolean;
};

const getIsMac = () => {
  if (typeof navigator === 'undefined') return false;
  const platform = navigator.platform.toLowerCase();
  const ua = navigator.userAgent.toLowerCase();

  return (
    platform.includes('mac') ||
    ua.includes('macintosh') ||
    ua.includes('mac os x')
  );
};

export const useSelectableListWeb = () => {
  const [selectedIds, setSelectedIds] = useState<Set<Id>>(() => new Set());

  const isMac = useMemo(getIsMac, []);

  const isMultiKey = (ne: NativeLikeEvent | undefined | null) => {
    if (!ne) return false;
    // mac: Shift / Command,  windows: Shift / Ctrl
    if (isMac) {
      return !!(ne.shiftKey || ne.metaKey);
    }
    return !!(ne.shiftKey || ne.ctrlKey);
  };

  const isSelected = (id: Id) => selectedIds.has(id);

  const clearSelection = () => {
    setSelectedIds(new Set());
  };

  const handleItemPress = (
    e: GestureResponderEvent & { nativeEvent: any },
    id: Id
  ) => {
    const ne = (e.nativeEvent || {}) as NativeLikeEvent;
    const multi = isMultiKey(ne);

    setSelectedIds((prev) => {
      if (multi) {
        const next = new Set(prev);
        if (next.has(id)) {
          next.delete(id);
        } else {
          next.add(id);
        }
        return next;
      }

      return new Set([id]);
    });
  };

  const handleContextMenuSelect = (
    e: GestureResponderEvent & { nativeEvent: any },
    id: Id
  ): Set<Id> => {
    const ne = (e.nativeEvent || {}) as NativeLikeEvent;
    const multi = isMultiKey(ne);
    let nextSelected = new Set<Id>();

    setSelectedIds((prev) => {
      if (multi) {
        nextSelected = new Set(prev);
        if (nextSelected.has(id)) {
          nextSelected.delete(id);
        } else {
          nextSelected.add(id);
        }
        return nextSelected;
      }

      if (prev.has(id)) {
        nextSelected = new Set(prev);
        return nextSelected;
      }

      nextSelected = new Set([id]);
      return nextSelected;
    });

    return nextSelected;
  };

  return {
    selectedIds,
    isSelected,
    clearSelection,
    setSelectedIds,
    handleItemPress,
    handleContextMenuSelect,
  };
};
